import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { ModalActionButtons } from '@/src/features/meter-view/components/modals/modal-action-buttons'
import { useUpdatePlannedStrategy } from '@/src/features/procurement-and-markets/details/hooks/usePlannedStrategies.hook'
import { mapToEnergyTypeDto } from '@/src/features/procurement-and-markets/details/types/adjustment.utils'
import {
  useLoadPlanningCurve,
  useLoadedStrategyMetadata,
} from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import { getLoadPlanningEnergyTypeLabel } from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { appColors } from '@/src/theme/foundations/colors'
import { formatMeasurement } from '@/src/utils/formatMeasurement'
import { Box, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react'
import { FlexCol, FlexRow } from '@components'
import { useTranslation } from 'react-i18next'

interface SaveLoadPlanningModalProps {
  isOpen: boolean
  onClose: () => void
  companyId?: number
  locationId?: number
  year?: number
  energyType?: string
  onSave?: (consumptionDifference: number) => void
}

export const SaveLoadPlanningModal = ({ isOpen, onClose, onSave }: SaveLoadPlanningModalProps) => {
  const { t } = useTranslation()

  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId: companyId } = useLocationsContext()

  // TODO - epr-915: Should implement a hook with the proper naming (update load planning)
  const { mutateAsync: saveLoadPlanning, isPending } = useUpdatePlannedStrategy()

  const { data: loadForecastData } = useLoadPlanningCurve({
    companyId,
    locationId: undefined,
    year: strategyMetadata?.year,
    energyType: mapToEnergyTypeDto(strategyMetadata?.energyType),
    granularity: GranularityEnum.ONE_MONTH,
  })

  const handleClose = () => {
    onClose()
  }

  const savedConsumption = loadForecastData?.kpis?.aggregatedSavedConsumption.energy.amount
  const draftConsumption = loadForecastData?.kpis?.aggregatedDraftConsumption.energy.amount
  const energyTypeLabel = getLoadPlanningEnergyTypeLabel(t, strategyMetadata?.energyType)
  const year = strategyMetadata?.year

  const handleSubmit = async () => {
    if (!strategyMetadata) {
      throw new Error('Strategy not loaded!')
    }
    try {
      await saveLoadPlanning({
        companyId: Number(companyId),
        energyType: mapToEnergyTypeDto(strategyMetadata?.energyType),
        year: Number(strategyMetadata.year),
        requestBody: {
          savedAt: new Date().toISOString(),
        },
      })
    } finally {
      const consumptionDifference = (draftConsumption ?? 0) - (savedConsumption ?? 0)
      onSave?.(consumptionDifference)
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='2xl' motionPreset='slideInBottom' isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('components.procurement.forecasting.save_modal.title', { energyType: energyTypeLabel, year })}</ModalHeader>
        <ModalCloseButton data-action='loadplan-saveplanmodal-closex' />

        <ModalBody>
          <FlexCol gap={4}>
            <Text fontSize='14px' color={appColors.gray[700]}>
              {t('components.procurement.forecasting.save_modal.description')}
            </Text>

            <Box>
              <FlexRow justifyContent='space-between' pb={2} px={3} borderBottom={`1px solid ${appColors.border}`}>
                <Text fontSize='14px' fontWeight={600} color={appColors.gray[700]}>
                  {t('components.procurement.forecasting.save_modal.label')}
                </Text>
                <Text fontSize='14px' fontWeight={600} color={appColors.gray[700]}>
                  {t('components.procurement.forecasting.save_modal.consumption')}
                </Text>
              </FlexRow>

              <FlexRow justifyContent='space-between' py={4} px={3} borderBottom={`1px solid ${appColors.border}`}>
                <Text fontSize='14px' fontWeight={400}>
                  {t('components.procurement.forecasting.save_modal.current_forecast')}
                </Text>
                <Text fontSize='14px' fontWeight={500}>
                  {formatMeasurement(savedConsumption, 'kWh', 1)}
                </Text>
              </FlexRow>

              <FlexRow justifyContent='space-between' py={4} px={3} bg={appColors.muted} borderBottom={`1px solid ${appColors.border}`}>
                <Text fontSize='14px' fontWeight={400}>
                  {t('components.procurement.forecasting.save_modal.edited_forecast')}
                </Text>
                <Text fontSize='14px' fontWeight={500}>
                  {formatMeasurement(draftConsumption, 'kWh', 1)}
                </Text>
              </FlexRow>
            </Box>
          </FlexCol>
        </ModalBody>

        <ModalFooter gap={'8px'} borderTop={`1px solid ${appColors.border}`}>
          <ModalActionButtons
            onCancel={handleClose}
            onSubmit={handleSubmit}
            submitText={t('components.procurement.forecasting.save_modal.submit_button')}
            cancelText={t('common.cancel')}
            isLoading={isPending}
            cancelDataAction='loadplan-saveplanmodal-cancel-action'
            saveDataAction='loadplan-saveplanmodal-save-action'
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
